import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { BorderlessFilterAccordion, FilterBadge, FilterClearIcon } from '@modules/search/index.styles'
import { HistoryFilterOptions } from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccordionDetails, Typography } from '@mui/material'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import SingleSelect from './SingleSelect'
import { FilterSectionAccordionSummary, FilterTitleBox, OuterFilterBox } from './index.styles'

export interface SearchByHistoryProps {
  searchHistoryTypeId: number
  setSearchHistoryTypeId: (searchHistoryTypeId: number | undefined) => void
  prefix: string
  onExpand: (value: boolean) => void
  isFilterExpanded: boolean
}

const SearchByHistory = ({
  searchHistoryTypeId,
  setSearchHistoryTypeId,
  isFilterExpanded,
  prefix,
  onExpand,
}: SearchByHistoryProps) => {
  const { t } = useTranslation()
  const { hasAccessToHistoryFiltering } = useFeatureFlags()

  const historyFilterOptions = useMemo(() => {
    const filterOptions: { value: Nullable<number>; label: string; id: string }[] = []

    if (!hasAccessToHistoryFiltering) {
      filterOptions.push({
        value: HistoryFilterOptions.PERSONAL_HISTORY as number,
        label: t(translationKeys.SEARCH_HISTORY_FILTER_PERSONAL),
        id: 'history-personal_history',
      })
    } else {
      filterOptions.push(
        {
          value: HistoryFilterOptions.MY_MEETINGS as number,
          label: t(translationKeys.SEARCH_HISTORY_FILTER_MY_MEETINGS),
          id: 'history-my_meetings',
        },
        {
          value: HistoryFilterOptions.COMPANY_MEETINGS as number,
          label: t(translationKeys.SEARCH_HISTORY_FILTER_COMPANY_HISTORY),
          id: 'history-company_history',
        },
      )
    }

    return [
      { label: t(translationKeys.NO_FILTER_APPLIED), value: null, id: 'history-all' },
      ...filterOptions,
      {
        value: HistoryFilterOptions.NO_HISTORY as number,
        label: t(translationKeys.SEARCH_HISTORY_FILTER_NO_HISTORY),
        id: 'history-no_history',
      },
    ]
  }, [hasAccessToHistoryFiltering, t])

  return (
    <OuterFilterBox $active={!!searchHistoryTypeId}>
      <BorderlessFilterAccordion disableGutters onChange={() => onExpand(!isFilterExpanded)} expanded={!!isFilterExpanded}>
        <FilterSectionAccordionSummary
          sx={{ flexDirection: 'row' }}
          expandIcon={<ExpandMoreIcon sx={{ transfrom: 'rotage(90deg)' }} />}
          id="search-filter-history_section"
          data-testid="filter_accordion_summary"
        >
          <FilterTitleBox>
            <Typography variant="h2">
              {t(translationKeys.SEARCH_BY_HISTORY)}
              {!!searchHistoryTypeId && (
                <FilterBadge variant="dot" color="primary" data-testid={`${prefix?.toLowerCase()}_filter_badge`} />
              )}
            </Typography>
            {!!searchHistoryTypeId && (
              <FilterClearIcon
                id="history_filter-reset"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  setSearchHistoryTypeId(undefined)
                }}
                color="primary"
                data-testid="filter_clear_section"
              />
            )}
          </FilterTitleBox>
        </FilterSectionAccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <SingleSelect
            value={searchHistoryTypeId || null}
            onChange={newValue => setSearchHistoryTypeId(Number(newValue) as unknown as number)}
            options={historyFilterOptions}
          />
        </AccordionDetails>
      </BorderlessFilterAccordion>
    </OuterFilterBox>
  )
}

export default memo(SearchByHistory)
